/**
 * The pane stack on phones and tablets (docs/design/visual-overhaul.md §12),
 * Discord's mobile model:
 *
 * - Phone: one pane at a time. "home" is the rail and the channel (or DM)
 *   list with the dock; "main" is the conversation, the voice room, Friends
 *   or Requests, slid over it full screen. Opening anything from the list
 *   (or from Jump, a profile, a notification) shows "main"; Back returns to
 *   "home".
 * - Tablet in portrait (nav "drawer"): the main pane always shows; the rail
 *   and sidebar are a left drawer (`navOpen`), closed by choosing something.
 *
 * The main pane and the drawer are on the kit's back stack
 * (@neosocial/ui/back), so Android's back button, iOS's swipe-back and the
 * browser's Back go back one step; the left-edge swipe does the same (Shell
 * installs it). Desktop: nothing here runs; the frame is the desktop design,
 * unchanged.
 */
import { goBack, pushBack } from '@neosocial/ui/back';
import { isCompact, onLayout, readLayout, useLayout } from '@neosocial/ui/layout';
import { create } from 'zustand';
import { useUi } from '../store/ui';

export { useLayout, readLayout, isCompact };
export type { Layout, LayoutState, PointerKind, NavMode } from '@neosocial/ui/layout';

export type Pane = 'home' | 'main';

interface PaneStore {
  /** Phone: the pane on screen. */
  pane: Pane;
  /** Tablet in portrait: the rail and sidebar drawer is open. */
  navOpen: boolean;
}

export const usePanes = create<PaneStore>(() => ({ pane: 'home', navOpen: false }));

let popMain: (() => void) | null = null;
let popNav: (() => void) | null = null;

/** Phone: shows the main pane (a Back returns to the list). */
export function openMain(): void {
  if (readLayout().layout !== 'phone') return;
  if (usePanes.getState().pane === 'main') return;
  usePanes.setState({ pane: 'main' });
  popMain = pushBack(() => {
    popMain = null;
    usePanes.setState({ pane: 'home' });
  });
}

/** Phone: back to the list (the home pane). */
export function showHome(): void {
  if (usePanes.getState().pane === 'home') return;
  usePanes.setState({ pane: 'home' });
  const p = popMain;
  popMain = null;
  p?.();
}

/** Tablet in portrait: opens the rail and sidebar drawer. */
export function openNav(): void {
  const l = readLayout();
  if (!(l.layout === 'tablet' && l.nav === 'drawer') || usePanes.getState().navOpen) return;
  usePanes.setState({ navOpen: true });
  popNav = pushBack(() => {
    popNav = null;
    usePanes.setState({ navOpen: false });
  });
}

export function closeNav(): void {
  if (!usePanes.getState().navOpen) return;
  usePanes.setState({ navOpen: false });
  const p = popNav;
  popNav = null;
  p?.();
}

/**
 * The app's own Back (the header's Back button, the left-edge swipe): the
 * top layer first (a sheet, a drawer), then the pane. A tablet in portrait
 * with nothing open opens its drawer instead (the edge swipe's job there).
 */
export function paneBack(): void {
  if (goBack()) return;
  const l = readLayout();
  if (l.layout === 'tablet' && l.nav === 'drawer') openNav();
}

/**
 * Follows the app's navigation: whatever changes the Hub view (a channel or
 * DM chosen in the list, Jump, a profile's "Message", joining voice) shows
 * the main pane on a phone and closes a tablet's drawer. The server rail is
 * the exception: Home and the server switch the list beside it, as Discord's
 * do. A cold start (no view yet) stays on the list.
 */
let wired = false;
export function wirePanes(): void {
  if (wired) return;
  wired = true;
  useUi.subscribe((s, prev) => {
    if (s.hub === prev.hub || prev.hub === null || s.hub === null) return;
    const l = readLayout();
    if (l.layout === 'desktop') return;
    // the rail's own buttons switch the list, they open nothing
    const ev = (globalThis as { event?: Event }).event;
    const from = ev?.target instanceof Element ? ev.target : null;
    if (from?.closest('.hub-rail')) return;
    if (l.layout === 'phone') openMain();
    else closeNav();
  });
  // a window that changes layout keeps a pane that exists there
  const settle = () => {
    const l = readLayout();
    if (l.layout !== 'phone') showHome();
    if (!(l.layout === 'tablet' && l.nav === 'drawer')) closeNav();
  };
  settle();
  onLayout(settle);
}
